"use client";

import { c as _c } from "react/compiler-runtime";
import { type ReactNode, useEffect, useRef, useState } from "react";
import { FaCakeCandles, FaLocationDot } from "react-icons/fa6";
import { useProgressNavigation } from "@/components/progress-bar";
import TagDisplay from "@/components/tags/tag-display";
import { useTenant } from "@/contexts/tenant-provider";
import { useLocations } from "@/hooks/use-locations";
import { formatDate, formatEmailOrHidden, formatName, formatPhoneNumberOrHidden } from "@/lib/format-data";
import { currentChartPatientId, isOnPatientChart } from "@/lib/patient-route";
import { SearchMatchText, searchMatchRanges } from "@/lib/search-match";
import { clickableText, cx } from "@/lib/tailwind-utils";
import { PatientSummary } from "@/types/patient";
import type { UserTiny } from "@/types/user";
import ClickablePhoneNumber from "./call-modal/clickable-phone-number";
import ClickableEmail from "./clickable-email";
import { MembershipBadges, isDisplayableMember, memberTinyToBadge } from "./membership-badges";
import { useClinicReportScope } from "@/api/analytics/use-clinic-report-scope";
import { patientSidebarDefinition } from "@/features/sidebar/patient-sidebar";
import { useRightSidebarStore } from "@/stores/right-sidebar-store";

/**
 * What this component actually reads: a user's identity plus a few optional
 * display fields. Typing it this way lets callers holding a `UserTiny` (the
 * Patient Status panel, for one) pass it straight through instead of casting
 * to `PatientSummary` — a cast that hid an `undefined` and crashed the page.
 */
export type PatientDisplayPatient = UserTiny & Partial<Pick<PatientSummary, "preferredName" | "pronouns" | "dateOfBirth" | "primaryLocationId" | "tags" | "memberships">>;
interface PatientDisplayProps {
  patient: PatientDisplayPatient;
  showPhoneNumber?: boolean;
  showDateOfBirth?: boolean;
  showEmail?: boolean;
  showLocation?: boolean;
  showMemberships?: boolean;
  showPronouns?: boolean;
  className?: string;
  nameClassName?: string;
  maxVisibleTags?: number;
  tagSize?: "sm" | "md" | "lg";
  layout?: "horizontal" | "vertical";
  useLinks?: boolean;
  onClick?: () => void;
  /** A search term whose matching letters are bolded in the name. */
  highlight?: string;
}
interface AdditionalInfoProps {
  patient: PatientDisplayPatient;
  showPhoneNumber: boolean;
  showDateOfBirth: boolean;
  showEmail: boolean;
  showLocation: boolean;
  showMemberships: boolean;
  showPronouns: boolean;
  useLinks: boolean;
  layout: "horizontal" | "vertical";
}
function AdditionalInfo(t0) {
  const $ = _c(39);
  const {
    patient,
    showPhoneNumber,
    showDateOfBirth,
    showEmail,
    showLocation,
    showMemberships,
    showPronouns,
    useLinks,
    layout
  } = t0;
  const {
    locations
  } = useLocations();
  let infoItems;
  if ($[0] !== locations || $[1] !== patient || $[2] !== showDateOfBirth || $[3] !== showEmail || $[4] !== showLocation || $[5] !== showMemberships || $[6] !== showPhoneNumber || $[7] !== showPronouns || $[8] !== useLinks) {
    infoItems = [];
    if (showPronouns && patient.pronouns) {
      let t1;
      if ($[10] !== patient.pronouns) {
        t1 = <span key="pronouns" className="text-caption">{patient.pronouns}</span>;
        $[10] = patient.pronouns;
        $[11] = t1;
      } else {
        t1 = $[11];
      }
      infoItems.push(t1);
    }
    if (showPhoneNumber && (patient.phoneNumber || patient.hasPhoneNumber)) {
      let t1;
      if ($[12] !== patient || $[13] !== useLinks) {
        t1 = useLinks ? <ClickablePhoneNumber key="phone" patient={patient} className="text-caption" /> : <span key="phone" className="text-caption">{formatPhoneNumberOrHidden(patient)}</span>;
        $[12] = patient;
        $[13] = useLinks;
        $[14] = t1;
      } else {
        t1 = $[14];
      }
      infoItems.push(t1);
    }
    if (showEmail && (patient.email || patient.hasEmail)) {
      let t1;
      if ($[15] !== patient || $[16] !== useLinks) {
        t1 = useLinks && patient.email ? <ClickableEmail key="email" email={patient.email} patientId={patient.id} /> : <span key="email" className="min-w-0 truncate text-caption">{formatEmailOrHidden(patient)}</span>;
        $[15] = patient;
        $[16] = useLinks;
        $[17] = t1;
      } else {
        t1 = $[17];
      }
      infoItems.push(t1);
    }
    if (showDateOfBirth && patient.dateOfBirth) {
      let t1;
      if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
        t1 = <FaCakeCandles size={12} className="shrink-0" />;
        $[18] = t1;
      } else {
        t1 = $[18];
      }
      let t2;
      if ($[19] !== patient.dateOfBirth) {
        t2 = formatDate(patient.dateOfBirth);
        $[19] = patient.dateOfBirth;
        $[20] = t2;
      } else {
        t2 = $[20];
      }
      let t3;
      if ($[21] !== t2) {
        t3 = <span key="dob" className="flex items-center gap-1 text-caption">{t1}<span>{t2}</span></span>;
        $[21] = t2;
        $[22] = t3;
      } else {
        t3 = $[22];
      }
      infoItems.push(t3);
    }
    if (showLocation && patient.primaryLocationId) {
      let t1;
      if ($[23] !== locations || $[24] !== patient) {
        let t2;
        if ($[26] !== patient) {
          t2 = loc => loc.id === patient.primaryLocationId;
          $[26] = patient;
          $[27] = t2;
        } else {
          t2 = $[27];
        }
        t1 = locations.find(t2);
        $[23] = locations;
        $[24] = patient;
        $[25] = t1;
      } else {
        t1 = $[25];
      }
      const location = t1;
      if (location) {
        let t2;
        if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
          t2 = <FaLocationDot size={12} className="shrink-0" />;
          $[28] = t2;
        } else {
          t2 = $[28];
        }
        let t3;
        if ($[29] !== location.name) {
          t3 = <span key="location" className="flex min-w-0 items-center gap-1 text-caption">{t2}<span className="truncate">{location.name}</span></span>;
          $[29] = location.name;
          $[30] = t3;
        } else {
          t3 = $[30];
        }
        infoItems.push(t3);
      }
    }
    if (showMemberships && patient.memberships && patient.memberships.some(isDisplayableMember)) {
      let t1;
      if ($[31] !== patient.memberships) {
        t1 = patient.memberships.filter(isDisplayableMember).map(memberTinyToBadge);
        $[31] = patient.memberships;
        $[32] = t1;
      } else {
        t1 = $[32];
      }
      let t2;
      if ($[33] !== t1) {
        t2 = <span key="memberships" className="flex flex-wrap items-center gap-1"><MembershipBadges badges={t1} /></span>;
        $[33] = t1;
        $[34] = t2;
      } else {
        t2 = $[34];
      }
      infoItems.push(t2);
    }
    $[0] = locations;
    $[1] = patient;
    $[2] = showDateOfBirth;
    $[3] = showEmail;
    $[4] = showLocation;
    $[5] = showMemberships;
    $[6] = showPhoneNumber;
    $[7] = showPronouns;
    $[8] = useLinks;
    $[9] = infoItems;
  } else {
    infoItems = $[9];
  }
  if (infoItems.length === 0) {
    return null;
  }
  if (layout === "vertical") {
    let t1;
    if ($[35] !== infoItems) {
      t1 = <div className="flex flex-col space-y-1 text-caption text-foreground-secondary">{infoItems.map(_temp)}</div>;
      $[35] = infoItems;
      $[36] = t1;
    } else {
      t1 = $[36];
    }
    return t1;
  }
  let t1;
  if ($[37] !== infoItems) {
    t1 = <div className="flex flex-wrap items-center gap-x-1 text-caption text-foreground-secondary">{infoItems.map(_temp2)}</div>;
    $[37] = infoItems;
    $[38] = t1;
  } else {
    t1 = $[38];
  }
  return t1;
}
function _temp2(info_0, index_0) {
  return <span key={index_0} className="flex items-center whitespace-nowrap">{index_0 > 0 && <span className="mx-1 text-foreground-secondary">•</span>}{info_0}</span>;
}
function _temp(info, index) {
  return <div key={index}>{info}</div>;
}
export default function PatientDisplay({
  patient,
  showPhoneNumber = false,
  showDateOfBirth = false,
  showEmail = false,
  showLocation = false,
  showMemberships = true,
  showPronouns = false,
  className,
  nameClassName,
  maxVisibleTags = 3,
  tagSize = "sm",
  layout = "horizontal",
  useLinks: useLinksProp = true,
  onClick,
  highlight
}: PatientDisplayProps) {
  const {
    tenant
  } = useTenant();
  // A patient on an HQ report belongs to one of its clinics, so HQ has no chart to open.
  const clinicReport = useClinicReportScope();
  const useLinks = useLinksProp && !clinicReport;
  const [shouldUseVertical, setShouldUseVertical] = useState(false);
  const horizontalRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (layout === "vertical") {
      setShouldUseVertical(true);
      return;
    }
    const checkOverflow = () => {
      if (horizontalRef.current && containerRef.current) {
        const container = containerRef.current;
        const content = horizontalRef.current;

        // Temporarily make content visible to measure it
        content.style.visibility = "hidden";
        content.style.position = "absolute";
        content.style.whiteSpace = "nowrap";
        const containerWidth = container.offsetWidth;
        const contentWidth = content.scrollWidth;

        // Reset styles
        content.style.visibility = "";
        content.style.position = "";
        content.style.whiteSpace = "";
        setShouldUseVertical(contentWidth > containerWidth - 20); // 20px buffer
      }
    };

    // Check immediately and on resize
    checkOverflow();
    const resizeObserver = new ResizeObserver(checkOverflow);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }
    return () => resizeObserver.disconnect();
  }, [layout, patient, showPhoneNumber, showDateOfBirth, showEmail, showLocation, showMemberships, showPronouns]);
  const Name = () => {
    const openSidebar = useRightSidebarStore(state => state.open);
    const {
      navigateWithProgress
    } = useProgressNavigation();
    const patientHref = `/${tenant?.subDomain}/patient/${patient?.id}`;
    const fullName = formatName(patient.firstName, patient.lastName);
    const nameRanges = highlight ? searchMatchRanges(fullName, highlight) : [];
    const preferredRanges = highlight && patient.preferredName ? searchMatchRanges(patient.preferredName, highlight) : [];
    // Matched letters go semibold, so the rest of the name drops to regular
    // weight to let them stand out.
    const highlighting = nameRanges.length + preferredRanges.length > 0;
    const nameContent = <>
        <SearchMatchText text={fullName} ranges={nameRanges} />
        {patient.preferredName && <span className="ml-1 font-normal text-foreground-secondary">
            (
            <SearchMatchText text={patient.preferredName} ranges={preferredRanges} />
            )
          </span>}
      </>;
    return useLinks ? <a className={cx(clickableText, highlighting && "font-normal", nameClassName)} onClick={e => {
      if (e.metaKey || e.ctrlKey) {
        window.open(patientHref, "_blank");
      } else if (currentChartPatientId() === patient?.id) {
        // Already reading this patient — nothing to open and nowhere to go.
      } else if (isOnPatientChart()) {
        // A chart already fills the screen, so peeking a second patient
        // beside it puts two different people on screen at once and reads
        // as a mistake. Go to the clicked patient instead — client-side, so
        // the app isn't torn down and the unsaved-changes guard still runs.
        navigateWithProgress(`/patient/${patient?.id}`);
      } else {
        openSidebar(patientSidebarDefinition, {
          patientTiny: patient
        });
      }
      onClick?.();
    }}>
        {nameContent}
      </a> : <span className={cx("truncate font-medium text-foreground", highlighting && "font-normal", nameClassName)}>
        {nameContent}
      </span>;
  };
  return <div ref={containerRef} className={cx("w-fit", className)}>
      {/* Hidden horizontal content for measurement */}
      {layout !== "vertical" && <div ref={horizontalRef} className="pointer-events-none invisible absolute left-0 top-0 flex items-center gap-2 overflow-hidden whitespace-nowrap">
          <Name />
          <TagDisplay tags={patient.tags || []} maxVisibleTags={maxVisibleTags} tagSize={tagSize} />
          <AdditionalInfo patient={patient} showPhoneNumber={showPhoneNumber} showDateOfBirth={showDateOfBirth} showEmail={showEmail} showLocation={showLocation} useLinks={useLinks} showMemberships={showMemberships} showPronouns={showPronouns} layout="horizontal" />
        </div>}

      {/* Actual visible content */}
      {shouldUseVertical ? <div className="flex flex-col">
          <div className="flex items-center gap-2">
            <Name />
            <TagDisplay tags={patient.tags || []} maxVisibleTags={maxVisibleTags} tagSize={tagSize} />
          </div>
          <AdditionalInfo patient={patient} showPhoneNumber={showPhoneNumber} showDateOfBirth={showDateOfBirth} showEmail={showEmail} showLocation={showLocation} useLinks={useLinks} showMemberships={showMemberships} showPronouns={showPronouns} layout="vertical" />
        </div> : <div className="flex items-center gap-2 overflow-hidden whitespace-nowrap">
          <Name />
          <TagDisplay tags={patient.tags || []} maxVisibleTags={maxVisibleTags} tagSize={tagSize} />
          <AdditionalInfo patient={patient} showPhoneNumber={showPhoneNumber} showDateOfBirth={showDateOfBirth} showEmail={showEmail} showLocation={showLocation} useLinks={useLinks} showMemberships={showMemberships} showPronouns={showPronouns} layout="horizontal" />
        </div>}
    </div>;
}